import React, { useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Package, CheckCircle, XCircle, Pill, Ban } from "lucide-react";
import api from "../../lib/api";

interface StatisticsProps {
  entityId: number | null;
  showOnlyOtc: boolean;
  showMrpZero?: boolean;
  statistics?: StatisticsData;
  loading?: boolean;
}

interface StatisticsData {
  totalProducts: number;
  mappedProducts: number;
  unmappedProducts: number;
  noMatchFoundProducts?: number;
  otcProducts: number;
  otcMappedProducts?: number;
  otcUnmappedProducts?: number;
  totalEntities?: number;
}

const ProductMappingStatistics: React.FC<StatisticsProps> = ({
  entityId,
  showOnlyOtc,
  showMrpZero = false,
  statistics: propStatistics,
  loading: propLoading
}) => {
  // Use props if provided, otherwise fetch own statistics
  const [localStatistics, setLocalStatistics] = useState<StatisticsData>({
    totalProducts: 0,
    mappedProducts: 0,
    unmappedProducts: 0,
    noMatchFoundProducts: 0,
    otcProducts: 0,
    otcMappedProducts: 0,
    otcUnmappedProducts: 0
  });
  const [localLoading, setLocalLoading] = useState(false);

  // Use prop statistics if provided, otherwise use local state
  const statistics = propStatistics || localStatistics;
  const loading = propLoading !== undefined ? propLoading : localLoading;

  useEffect(() => {
    // Only fetch if statistics are not provided as props
    if (!propStatistics) {
      fetchStatistics();
    }
  }, [entityId, showOnlyOtc, showMrpZero, propStatistics]);

  const fetchStatistics = async () => {
    setLocalLoading(true);
    try {
      // Pass null as entityId to get overall statistics when no entity is selected
      const response = await api.getProductMappingStatistics(entityId, showOnlyOtc, showMrpZero);
      if (response.success) {
        setLocalStatistics(response.data);
      }
    } catch (error) {
      // Error fetching statistics
    } finally {
      setLocalLoading(false);
    }
  };


  const mappingPercentage = statistics.totalProducts > 0
    ? Math.round((statistics.mappedProducts / statistics.totalProducts) * 100)
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {/* Total Products Card */}
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Total Products
            </p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
              {loading ? "..." : statistics.totalProducts.toLocaleString()}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {entityId ? "Entity products" : "Across all entities"}
            </p>
          </div>
          <div className="p-2 bg-blue-200 dark:bg-blue-800 rounded-lg">
            <Package className="w-5 h-5 text-blue-700 dark:text-blue-300" />
          </div>
        </div>
      </Card>

      {/* Unmapped Products Card - 2nd position */}
      <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Unmapped Products
            </p>
            <p className="text-2xl font-bold text-amber-900 dark:text-amber-100 mt-1">
              {loading ? "..." : statistics.unmappedProducts.toLocaleString()}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Needs mapping
            </p>
          </div>
          <div className="p-2 bg-amber-200 dark:bg-amber-800 rounded-lg">
            <XCircle className="w-5 h-5 text-amber-700 dark:text-amber-300" />
          </div>
        </div>
      </Card>

      {/* Mapped Products Card - 3rd position */}
      <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-green-700 dark:text-green-300">
              Mapped Products
            </p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">
              {loading ? "..." : statistics.mappedProducts.toLocaleString()}
            </p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {mappingPercentage}% complete
            </p>
          </div>
          <div className="p-2 bg-green-200 dark:bg-green-800 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-700 dark:text-green-300" />
          </div>
        </div>
      </Card>

      {/* No Match Found Products Card */}
      <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">
              No Match Found
            </p>
            <p className="text-2xl font-bold text-red-900 dark:text-red-100 mt-1">
              {loading ? "..." : (statistics.noMatchFoundProducts || 0).toLocaleString()}
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Cannot be matched
            </p>
          </div>
          <div className="p-2 bg-red-200 dark:bg-red-800 rounded-lg">
            <Ban className="w-5 h-5 text-red-700 dark:text-red-300" />
          </div>
        </div>
      </Card>

      {/* OTC Products Card */}
      <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
              OTC Products
            </p>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 mt-1">
              {loading ? "..." : statistics.otcProducts.toLocaleString()}
            </p>
            <div className="flex gap-3 text-xs text-purple-600 dark:text-purple-400 mt-1">
              {showOnlyOtc ? (
                <>
                  <span>Total OTC</span>
                </>
              ) : (
                <>
                  <span className="text-green-600">
                    {statistics.otcMappedProducts?.toLocaleString() || '0'} mapped
                  </span>
                  <span className="text-amber-600">
                    {statistics.otcUnmappedProducts?.toLocaleString() || '0'} unmapped
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="p-2 bg-purple-200 dark:bg-purple-800 rounded-lg">
            <Pill className="w-5 h-5 text-purple-700 dark:text-purple-300" />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductMappingStatistics;